/**
 * Runtime capability detection (docs/02 "Platform layer"). Features ask what this device can do;
 * nothing branches on browser names.
 */

export interface Capabilities {
  secureContext: boolean;
  webgl2: boolean;
  audioWorklet: boolean;
  offscreenCanvas: boolean;
  microphone: boolean;
  tabAudio: boolean;
  fullscreen: boolean;
  wakeLock: boolean;
  webMidi: boolean;
  windowManagement: boolean;
  sharedArrayBuffer: boolean;
  outputSelection: boolean;
}

export function detectCapabilities(): Capabilities {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const md = nav?.mediaDevices;
  let webgl2: boolean;
  try {
    webgl2 = !!document.createElement('canvas').getContext('webgl2');
  } catch {
    webgl2 = false;
  }
  const doc = document as Document & { webkitFullscreenEnabled?: boolean };
  return {
    secureContext: window.isSecureContext,
    webgl2,
    audioWorklet: typeof AudioWorkletNode !== 'undefined',
    offscreenCanvas:
      typeof OffscreenCanvas !== 'undefined' &&
      'transferControlToOffscreen' in HTMLCanvasElement.prototype,
    microphone: !!md?.getUserMedia,
    tabAudio: !!md?.getDisplayMedia,
    fullscreen: !!(doc.fullscreenEnabled || doc.webkitFullscreenEnabled),
    wakeLock: !!nav && 'wakeLock' in nav,
    webMidi: !!nav && 'requestMIDIAccess' in nav,
    windowManagement: 'getScreenDetails' in window,
    sharedArrayBuffer:
      typeof SharedArrayBuffer !== 'undefined' && window.crossOriginIsolated === true,
    outputSelection: typeof AudioContext !== 'undefined' && 'setSinkId' in AudioContext.prototype,
  };
}

/** Requirements for the core experience (docs/08 "Support matrix"). */
export function missingRequirements(c: Capabilities): string[] {
  const missing: string[] = [];
  if (!c.secureContext) missing.push('a secure (HTTPS) connection');
  if (!c.webgl2) missing.push('WebGL 2 graphics');
  if (!c.audioWorklet) missing.push('Web Audio AudioWorklet');
  return missing;
}
